import { Component } from '@theme/component';

/**
 * Includ Engagement — a Reels-style vertical video feed.
 *
 * All markup lives in the section (includ-engagement.liquid) as static HTML +
 * <template> elements. This script only:
 *   1. reads/normalises the data on `window.includeEngagementData`
 *   2. clones a <template> per reel / tab / chip and fills in the data
 *   3. wires clicks (via one delegated listener) and video autoplay
 *
 * So to change how a reel *looks*, edit the Liquid template — not this file.
 * Flip DEBUG to trace lifecycle + playback decisions in the console.
 */

const DEBUG = false;
const NS = '[includ-engagement]';
const log = (...args) => DEBUG && console.log(NS, ...args);
const warn = (...args) => console.warn(NS, ...args);

/** Sentinel filter value meaning "no filter / show everything". */
const ALL_TAG = '__all__';

/** URL query param that selects / reflects the current reel by its id. */
const ENTRY_PARAM = 'video';

const CONTENT_PARAM = 'content';

const CONTENT_TYPES = ['PRODUCTS', 'COLLECTION_ID', 'BLOG'];

/** Wrapper values for `?content=` */
const CONTENT_WRAPPER_SHOPPABLE = 'shoppable';

const CONTENT_WRAPPER_NON_SHOPPABLE = 'non_shoppable';

/** How many lines a caption shows before it gets a "more" toggle. */
const CAPTION_LINES = 3;

/**
 * How long the feed must stop moving before we do the expensive per-reel work
 * (load source, play, rewrite the URL). Long enough to skip reels you flick past,
 * short enough to feel immediate once you land.
 */
const SETTLE_MS = 120;

/* ------------------------------------------------------------------ *
 * Data layer — turn the raw payload into clean, safe reels.
 * ------------------------------------------------------------------ */

/**
 * @typedef {Object} Reel
 * @property {string} id             The reel's ONLY identity, from the metafield's
 *                                   `id` field. Deliberately NOT the array position:
 *                                   deleting or reordering a reel must not change
 *                                   which video a `?video=` link opens, or which
 *                                   reel a saved like belongs to.
 *                                   Always a string — URL params are strings, so
 *                                   normalising once avoids 12 === "12" bugs.
 * @property {string} url            Video source URL (may be an HLS .m3u8).
 * @property {string} fallback       MP4 rendition — preferred source for playback.
 * @property {string} poster         Optional thumbnail — shown as the <video> poster
 *                                   (kills the black frame) and as end-card avatars.
 * @property {number} likes          Hard-coded base like count from the metafield (read-only).
 * @property {number} share          Hard-coded base share count from the metafield (read-only).
 * @property {string} caption        Optional caption text (clamped to 3 lines).
 * @property {string[]} tags         Cleaned, de-duplicated tags.
 * @property {object} content        conent - type and values {type: string, values: string[]}|null
 */

const EngagementData = {
  /**
   * Normalise `window.includeEngagementData` into reels. Malformed records are
   * dropped (not thrown) so one bad entry never blanks the whole feed.
   * @param {unknown} raw
   * @returns {Reel[]}
   */
  parse(raw) {
    const list = Array.isArray(raw?.reels) ? raw.reels : [];
    const reels = list.map(EngagementData.normalise).filter(Boolean);

    // Duplicate ids make deep links ambiguous and would apply one like to several
    // reels. It's a data error — surface it rather than silently picking one.
    const seen = new Set();
    for (const reel of reels) {
      if (seen.has(reel.id)) warn(`duplicate id "${reel.id}" — deep links to it are ambiguous`);
      seen.add(reel.id);
    }

    log(`parsed ${reels.length}/${list.length} reels`);
    return reels;
  },

  /**
   * @param {any} reel
   * @returns {Reel|null}
   */
  normalise(reel) {
    const url = typeof reel?.url === 'string' ? reel.url.trim() : '';
    const fallback = typeof reel?.fallback === 'string' ? reel.fallback.trim() : '';

    // Either source alone is playable — the .m3u8 (adaptive) or the .mp4 — so only
    // skip a reel when it has neither. Requiring `url` would drop mp4-only reels.
    if (!url && !fallback) {
      warn('skipping reel with no playable source (needs url or fallback)', reel);
      return null;
    }

    // `id` is the reel's identity — without it we can't deep-link to it or key a
    // like on it, so skip it loudly rather than half-work.
    const id = reel.id == null ? '' : String(reel.id).trim();
    if (!id) {
      warn('skipping reel without an id', reel);
      return null;
    }

    const rawTags = Array.isArray(reel.tags) ? reel.tags : [];
    const tags = [...new Set(rawTags.filter((t) => typeof t === 'string' && t.trim()).map((t) => t.trim()))];

    const poster = typeof reel.poster === 'string' ? reel.poster.trim() : '';

    const likes = toCount(reel.likes);
    // Accept `shares` (current) or `share` (earlier payloads) so either shape works.
    const share = toCount(reel.shares ?? reel.share);

    const caption = typeof reel.caption === 'string' ? reel.caption.trim() : '';

    return {
      id,
      url,
      fallback,
      poster,
      likes,
      share,
      caption,
      tags,
      content: EngagementData.normaliseContent(reel.content)
    };
  },

  /**
   * Normalise the raw `content` field into `{ type, values }` or null.
   * DEFAULT (or a type with no values) collapses to null so callers only ever
   * need to check truthiness, never type + length separately.
   * @param {any} content
   * @returns {{type: string, values: string[]}|null}
   */

  normaliseContent(content) {
    const type = typeof content?.type === 'string' ? content.type.trim() : '';
    if (!type || type === 'DEFAULT') return null;

    const raw = Array.isArray(content?.value) ? content.value : [];
    const values = raw.filter((v) => typeof v === 'string' && v.trim()).map((v) => v.trim());
    if (values.length === 0) return null;

    return { type, values };
  },

  /**
   * Ordered list of every unique tag across reels (for the filter bar).
   * @param {Reel[]} reels
   * @returns {string[]}
   */
  uniqueTags(reels) {
    const set = new Set();
    for (const reel of reels) for (const tag of reel.tags) set.add(tag);
    return [...set];
  },

  /**
   * Resolve the entry reel from a raw `?video=` param value.
   * Returns the matching reel `id`, or `null` when the param is missing or
   * doesn't match any reel (e.g. that reel was deleted from the metafield).
   * `null` means "no anchor" → the caller shuffles everything.
   *
   * @param {Reel[]} reels
   * @param {string|null} rawParam
   * @returns {string|null}
   */
  resolveEntryId(reels, rawParam) {
    const id = rawParam == null ? '' : rawParam.trim();
    if (!id) return null;

    const exists = reels.some((reel) => reel.id === id);
    if (!exists) {
      warn(`no reel with id "${id}" — shuffling instead`);
      return null;
    }
    return id;
  },

  /**
 * Resolve the content-type filter from a raw `?content=` param value.
 *   - missing / empty            -> null   (no filter)
 *   - matches a known type       -> that type, e.g. "PRODUCTS" (case-insensitive input ok)
 *   - present but unrecognised   -> 'ANY'  (any reel that has a content object at all)
 * @param {string|null} rawParam
 * @returns {string|null}
 */
  resolveContentFilter(rawParam) {
    const value = rawParam == null ? '' : rawParam.trim();
    if (!value) return null;

    if (CONTENT_TYPES.includes(value)) return value;
    if (value === CONTENT_WRAPPER_SHOPPABLE) return value;
    if (value === CONTENT_WRAPPER_NON_SHOPPABLE) return value;

    warn(`unrecognised ?content= value "${value}" — ignoring, no content filter applied`);
    return null;
  },

  /**
   * Build the play order:
   *   - entryId is null  -> everything shuffled (no anchor)
   *   - entryId is set   -> that reel first, then the rest shuffled
   * Pure: returns a NEW array and never mutates the input.
   *
   * @param {Reel[]} reels
   * @param {string|null} entryId
   * @param {() => number} [rng]  injectable for deterministic tests
   * @returns {Reel[]}
   */
  order(reels, entryId, rng = Math.random) {
    if (entryId == null) return shuffle(reels, rng);

    const entry = reels.find((reel) => reel.id === entryId);
    if (!entry) return shuffle(reels, rng); // defensive; resolveEntryId already guards

    const rest = reels.filter((reel) => reel !== entry);
    return [entry, ...shuffle(rest, rng)];
  },
};

 /**
   * Pull the reels array out of a metafield payload. Defensive against either
   * shape the backend might send — a bare array, or `{ reels: [...] }` like the
   * old single-metafield format.
   * @param {unknown} raw
   * @returns {unknown[]}
   */
  function extractReels(raw) {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.reels)) return raw.reels;
    return [];
  }


/**
 * Fisher–Yates shuffle. Returns a NEW array; does not mutate the input.
 * (Not `sort(() => Math.random() - 0.5)` — that is statistically biased.)
 * @template T
 * @param {T[]} input
 * @param {() => number} [rng]  injectable for deterministic tests
 * @returns {T[]}
 */
function shuffle(input, rng = Math.random) {
  const array = [...input];
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Return `href` with the `?video=` param set to `id`. Pure and testable;
 * preserves every other query param.
 * @param {string} href
 * @param {number|string} id
 * @returns {string}
 */
function withVideoParam(href, id) {
  const url = new URL(href);
  url.searchParams.set(ENTRY_PARAM, String(id));
  return url.toString();
}

/**
 * Coerce a raw metafield count to a safe non-negative integer (0 if missing/junk).
 * @param {unknown} value
 * @returns {number}
 */
function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

/**
 * Compact count label, e.g. 656 -> "656", 1210 -> "1.2k".
 * @param {number} n
 * @returns {string}
 */
function formatCount(n) {
  return n >= 1000 ? `${(n / 1000).toFixed(1).replace('.0', '')}k` : String(n);
}

/**
 * Client-side "liked" flags, persisted in localStorage.
 *
 * IMPORTANT: this stores only WHICH reels were liked (their `id`) — never the
 * counts. The count is always recomputed as
 * `reel.likes (metafield) + (liked ? 1 : 0)`, so editing `likes` in the JSON
 * always wins and can never be shadowed by stale saved numbers.
 *
 * Keyed on `id` (not array position) so deleting or reordering reels can't move
 * someone's likes onto the wrong videos.
 *
 * Nothing is ever sent to a server: the count a visitor sees move is their own.
 */
const LIKES_STORAGE_KEY = 'includ-engagement:likes';

const LikeStore = {
  /** @returns {Set<string>} liked reel ids */
  load() {
    try {
      const raw = localStorage.getItem(LIKES_STORAGE_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(list) ? list.map(String) : []);
    } catch (error) {
      warn('could not read likes from localStorage', error);
      return new Set(); // private mode etc. — fall back to in-memory
    }
  },

  /** @param {Set<string>} liked */
  save(liked) {
    try {
      localStorage.setItem(LIKES_STORAGE_KEY, JSON.stringify([...liked]));
    } catch (error) {
      warn('could not save likes to localStorage', error);
    }
  },
};

/**
 * Client-side share tallies, persisted in localStorage.
 *
 * Unlike likes (a flag you can toggle off), a share is a TALLY: every tap counts
 * and it never goes down. So this stores `{ [id]: timesSharedByThisVisitor }`
 * rather than a Set.
 *
 * Same rule as likes: the base count stays in the metafield and is never stored,
 * so the displayed number is always `reel.share + timesSharedHere` and editing
 * the JSON always wins. Nothing is sent to a server.
 */
const SHARES_STORAGE_KEY = 'includ-engagement:shares';

const ShareStore = {
  /** @returns {Map<string, number>} id -> times shared by this visitor */
  load() {
    try {
      const raw = localStorage.getItem(SHARES_STORAGE_KEY);
      const object = raw ? JSON.parse(raw) : {};
      if (!object || typeof object !== 'object' || Array.isArray(object)) return new Map();
      return new Map(
        Object.entries(object)
          .map(([id, count]) => [String(id), toCount(count)])
          .filter(([, count]) => count > 0)
      );
    } catch (error) {
      warn('could not read shares from localStorage', error);
      return new Map(); // private mode etc. — fall back to in-memory
    }
  },

  /** @param {Map<string, number>} shared */
  save(shared) {
    try {
      localStorage.setItem(SHARES_STORAGE_KEY, JSON.stringify(Object.fromEntries(shared)));
    } catch (error) {
      warn('could not save shares to localStorage', error);
    }
  },
};

 /**
 * Fetches and caches lightweight previews (title/image/price/url) for the
 * paths in a reel's `content.values`.
 *
 * Products use Shopify's documented `/products/{handle}.js` endpoint, which
 * returns real title/price/image. Collections and blog articles have no
 * equivalent endpoint, so we fetch the page itself and read its Open Graph
 * tags instead — every page the theme's layout renders includes those for
 * SEO, so one code path covers all three content types.
 *
 * Cached per path for the page's lifetime: a tagged product/collection/post
 * won't change out from under a visitor mid-session.
 */
  const ContentPreview = {
    /** @type {Map<string, Promise<{title: string, image: string, url: string, price: string|null}|null>>} */
    cache: new Map(),

    /** @param {string} path */
    fetch(path) {
      if (!this.cache.has(path)) this.cache.set(path, this._load(path));
      return this.cache.get(path);
    },

    /** @param {string} path */
    async _load(path) {
      try {
        return path.startsWith('/products/') ? await this._loadProduct(path) : await this._loadPage(path);
      } catch (error) {
        warn('content preview failed for', path, error);
        return null;
      }
    },

    /** @param {string} path */
   async _loadProduct(path) {
    const response = await fetch(`${path}.js`);
    if (!response.ok) return null;
    const product = await response.json();
    return {
      title: product.title ?? '',
      image: product.featured_image ?? product.images?.[0] ?? '',
      url: product.url ?? path,
      price: typeof product.price === 'number' ? formatMoney(product.price) : null,
      compare: typeof product.compare_at_price === 'number' && product.compare_at_price > product.price ? formatMoney(product.compare_at_price) : null,
    };
   },

    /** @param {string} path */
    async _loadPage(path) {
      const response = await fetch(path);
      if (!response.ok) return null;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const og = (property) => doc.querySelector(`meta[property="${property}"]`)?.content ?? '';
      return { title: og('og:title') || doc.title || path, image: og('og:image'), url: path, price: null };
    },

    labelFor(type) {
      switch (type) {
        case 'PRODUCTS': return 'Shop this video';
        case 'COLLECTION_ID': return 'Featured collection';
        case 'BLOG': return 'From the blog';
        default: return 'More to explore';
      }
    },
  };
  /**
   * Format integer cents using the shop's active currency. `Shopify.currency` is
   * a standard global every Shopify storefront sets via `content_for_header`, so
   * this needs no theme-specific setup; falls back to a plain "$12.34" if it's
   * ever missing (e.g. this script runs somewhere content_for_header didn't).
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active ?? 'USD';
    try {
      return new Intl.NumberFormat(navigator.language, { style: 'currency', currency }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }


/* ------------------------------------------------------------------ *
 * View component
 * ------------------------------------------------------------------ */

class IncludEngagementComponent extends Component {
  /** @type {Reel[]} */ #reels = [];
  /** @type {string[]} */ #tags = [];
  /** Active filter tag, or ALL_TAG. */ #activeTag = ALL_TAG;
  /** Active content-type filter: null | 'ANY' | 'PRODUCTS' | 'COLLECTION_ID' | 'BLOG'. */ #activeContentType = null;
  /** Global mute state (videos start muted so autoplay is allowed). */ #muted = true;
  /** @type {Set<string>} liked reels, by id (localStorage-backed). */ #liked = new Set();
  /** @type {Map<string, number>} id -> times this visitor shared it (localStorage-backed). */ #shared = new Map();
  /** @type {Set<string>} reels we've already sent a `video_like` GA event for (per session). */ #emittedLike = new Set();
  /** @type {HTMLElement|null} */ #activeReel = null;
  /** @type {IntersectionObserver|null} */ #observer = null;
  /** Last id written to the URL, to skip redundant history writes. */ #syncedId = null;
  /** @type {number|undefined} debounce handle for re-clamping captions on resize. */ #resizeTimer;
  /** @type {number|undefined} debounce handle for post-scroll work (see #setActiveReel). */ #settleTimer;
  /** @type {Map<string, {type: string, values: string[]}>} reel id -> its content, for reels that have any. */ #contentById = new Map();

  // Cached references to the static shell (see the Liquid template).
  #els = {
    tabs: /** @type {HTMLElement|null} */ (null),
    viewport: /** @type {HTMLElement|null} */ (null),
    mute: /** @type {HTMLElement|null} */ (null),
  };

  connectedCallback() {
    super.connectedCallback();

    this.#els.tabs = this.querySelector('[data-ie-tabs]');
    this.#els.viewport = this.querySelector('[data-ie-viewport]');
    this.#els.mute = this.querySelector('[data-ie-mute]');

    // ?content= decides which raw dataset backs the feed at all — resolved FIRST,
    // before we even parse. Links shared before shoppable existed carry no
    // ?content= and must keep resolving against the regular reels only; the two
    // sources are never merged. Shoppable only loads when explicitly asked for.
    const rawContentParam = new URLSearchParams(window.location.search).get(CONTENT_PARAM);
    this.#activeContentType = EngagementData.resolveContentFilter(rawContentParam);

    const wantsShoppable = this.#activeContentType === CONTENT_WRAPPER_SHOPPABLE || CONTENT_TYPES.includes(this.#activeContentType);

    const source = wantsShoppable
      ? extractReels(window.includeEngagementShoppableData)
      : extractReels(window.includeEngagementData);

    const parsed = EngagementData.parse({ reels: source });

    this.#liked = LikeStore.load();
    this.#shared = ShareStore.load();
    const known = new Set(parsed.map((reel) => reel.id));

    let prunedLikes = false;
    for (const id of this.#liked) {
      if (!known.has(id)) {
        this.#liked.delete(id);
        prunedLikes = true;
      }
    }
    if (prunedLikes) LikeStore.save(this.#liked);

    let prunedShares = false;
    for (const id of this.#shared.keys()) {
      if (!known.has(id)) {
        this.#shared.delete(id);
        prunedShares = true;
      }
    }
    if (prunedShares) ShareStore.save(this.#shared);

    // Decide which reel opens first, then order the feed:
    //   ?video=<id> valid        -> that reel first, rest shuffled
    //   missing / invalid            -> everything shuffled
    const rawParam = new URLSearchParams(window.location.search).get(ENTRY_PARAM);
    const entryId = EngagementData.resolveEntryId(parsed, rawParam);
    this.#reels = EngagementData.order(parsed, entryId);
    this.#tags = EngagementData.uniqueTags(this.#reels);
    this.#contentById = new Map(this.#reels.filter((reel) => reel.content).map((reel) => [reel.id, reel.content]));

    this.addEventListener('click', this.#onClick);
    this.#renderTabs();
    this.#renderReels();

    // The theme swaps in a custom font (Albert Sans). If captions were measured
    // before it loaded, the clamp point would be off — re-clamp once it's ready.
    // (#clampCaption skips captions the visitor has already expanded.)
    document.fonts?.ready?.then(() => this.#clampCaptions());

    // The clamp is measured against the current width, so it has to be redone
    // when that changes (phone rotation, desktop resize).
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#teardownVideos();
    window.removeEventListener('resize', this.#onResize);
    clearTimeout(this.#resizeTimer);
    clearTimeout(this.#settleTimer);
  }

  /** Debounced: re-measure captions after the viewport settles. */
  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#clampCaptions(), 150);
  };

  /** Reels matching the current tag filter AND content-type filter (if any). */
  get #visibleReels() {
    let reels = this.#activeTag === ALL_TAG
      ? this.#reels
      : this.#reels.filter((reel) => reel.tags.includes(this.#activeTag));

    if (this.#activeContentType === CONTENT_WRAPPER_SHOPPABLE) {
      reels = reels.filter((reel) => reel.content);
    } else if (this.#activeContentType === CONTENT_WRAPPER_NON_SHOPPABLE) {
      reels = reels.filter((reel) => !reel.content);
    } else if (this.#activeContentType) {
      reels = reels.filter((reel) => reel.content?.type === this.#activeContentType);
    }

    return reels;
  }

  /**
   * Clone the `<template data-ie-{name}>` markup from the Liquid section.
   * @param {string} name
   * @returns {HTMLElement}
   */
  #clone(name) {
    const template = this.querySelector(`template[data-ie-${name}]`);
    return /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
  }

  /* ----------------------------- render ---------------------------- */

  #renderTabs() {
    const tabs = this.#els.tabs;
    if (!tabs) return;

    if (this.#tags.length === 0) {
      tabs.hidden = true;
      return;
    }

    const makeTab = (label, tag) => {
      const el = this.#clone('tab');
      el.dataset.ieTag = tag;
      el.querySelector('[data-ie-tab-label]').textContent = label;
      el.classList.toggle('is-active', this.#activeTag === tag);
      return el;
    };

    tabs.replaceChildren(makeTab('All', ALL_TAG), ...this.#tags.map((tag) => makeTab(`#${tag}`, tag)));
  }

  #renderReels() {
    const viewport = this.#els.viewport;
    if (!viewport) return;

    this.#teardownVideos(); // pause videos from the previous render

    const reels = this.#visibleReels;
    if (reels.length === 0) {
      viewport.replaceChildren(this.#clone('empty'));
      return;
    }

    // The end card always sits after the (filtered) reels — it's the final scroll.
    viewport.replaceChildren(...reels.map((reel) => this.#buildReel(reel)), this.#buildEndCard());
    viewport.scrollTop = 0;
    this.#clampCaptions(); // must run once the reels are in the DOM — it measures height
    this.#observe();
  }

  /* ---------------------------- captions --------------------------- */

  /** Clamp every caption currently in the feed. */
  #clampCaptions() {
    for (const caption of this.#els.viewport?.querySelectorAll('[data-ie-caption]') ?? []) {
      this.#clampCaption(/** @type {HTMLElement} */ (caption));
    }
  }

  /**
   * Clamp a caption to CAPTION_LINES with an inline "more" at the end of the
   * last line. We truncate manually rather than relying on -webkit-line-clamp
   * so the button sits *inline* with the text instead of on its own line.
   * @param {HTMLElement} caption
   */
  #clampCaption(caption) {
    if (caption.dataset.expanded === 'true') return; // user opened it — leave it alone

    const full = caption.dataset.caption ?? '';
    if (!full) return;

    caption.classList.remove('ie__clamp'); // drop the CSS fallback; we truncate by hand now
    caption.textContent = full;

    const lineHeight = parseFloat(getComputedStyle(caption).lineHeight) || 20;
    const maxHeight = lineHeight * CAPTION_LINES + 2;
    if (caption.scrollHeight <= maxHeight) return; // already fits — no toggle needed

    // End offset of every word. We binary-search these and SLICE the original
    // string rather than join words back together, so the caption's own
    // whitespace — including \n / \n\n paragraph breaks — survives truncation.
    const cuts = [];
    for (const match of full.matchAll(/\S+/g)) cuts.push(match.index + match[0].length);
    if (cuts.length === 0) return;

    // Longest word-prefix that still fits in CAPTION_LINES *with* the "more" button.
    let lo = 1;
    let hi = cuts.length - 1;
    let best = cuts[0];
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      this.#renderCaptionClamped(caption, cuts[mid], full);
      if (caption.scrollHeight <= maxHeight) {
        best = cuts[mid];
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    this.#renderCaptionClamped(caption, best, full);
  }

  /**
   * @param {HTMLElement} caption
   * @param {number} cut   character offset to truncate the original text at
   * @param {string} full
   */
  #renderCaptionClamped(caption, cut, full) {
    caption.dataset.expanded = 'false';
    // trimEnd so a cut landing on a line break doesn't push "…more" onto its own line.
    caption.textContent = `${full.slice(0, cut).trimEnd()}… `;
    const more = this.#clone('more');
    more.textContent = 'more';
    more.addEventListener('click', () => this.#expandCaption(caption, cut, full));
    caption.append(more);
  }

  /**
   * @param {HTMLElement} caption
   * @param {number} cut   offset to restore when collapsing again
   * @param {string} full
   */
  #expandCaption(caption, cut, full) {
    caption.dataset.expanded = 'true';
    caption.textContent = `${full} `;
    const less = this.#clone('more');
    less.textContent = 'show less';
    less.addEventListener('click', () => this.#renderCaptionClamped(caption, cut, full));
    caption.append(less);
  }

  /**
   * The end card shown once every reel has been seen. It reads the current
   * filter: "all caught up" for the whole feed, "that's all for #tag" for a
   * slice — always offering a way forward.
   * @returns {HTMLElement}
   */
  #buildEndCard() {
    const el = this.#clone('end');
    const filtered = this.#activeTag !== ALL_TAG;

    // Avatar stack from the first few reels' posters (falls back to the CSS colour).
    const avas = el.querySelector('[data-ie-end-avas]');
    for (const reel of this.#reels.slice(0, 4)) {
      const ava = this.#clone('ava');
      if (reel.poster) ava.style.backgroundImage = `url(${reel.poster})`;
      avas.append(ava);
    }

    /** @type {[string, string][]} label -> tag */
    const jumps = [];

    if (filtered) {
      el.querySelector('[data-ie-end-title]').textContent = `That’s all for #${this.#activeTag}`;
      el.querySelector('[data-ie-end-sub]').textContent =
        'Want more? Jump back into everything, or try another topic.';
      el.querySelector('[data-ie-end-jumpslabel]').textContent = 'Keep exploring';
      jumps.push(['All videos', ALL_TAG]);
      for (const tag of this.#tags.filter((t) => t !== this.#activeTag).slice(0, 2)) jumps.push([`#${tag}`, tag]);
    } else {
      el.querySelector('[data-ie-end-title]').textContent = 'You’re all caught up';
      el.querySelector('[data-ie-end-sub]').textContent =
        'You’ve watched everything new this week. Fresh videos every week.';
      el.querySelector('[data-ie-end-jumpslabel]').textContent = 'Or explore a topic';
      for (const tag of this.#tags.slice(0, 3)) jumps.push([`#${tag}`, tag]);
    }

    const chips = el.querySelector('[data-ie-end-chips]');
    for (const [label, tag] of jumps) {
      const jump = this.#clone('endjump');
      jump.dataset.ieTag = tag; // reuses the shared [data-ie-tag] click handler
      jump.textContent = label;
      chips.append(jump);
    }

    return el;
  }

  /**
   * @param {Reel} reel
   * @returns {HTMLElement}
   */
  #buildReel(reel) {
    const el = this.#clone('reel');
    el.dataset.id = reel.id;

    const video = /** @type {HTMLVideoElement} */ (el.querySelector('.ie__video'));
    video.muted = true; // must be a property, not just an attribute, for autoplay
    // Lazy: URLs sit on data-* until this reel (or the one right after it) becomes
    // active — see #loadVideo — so we never fetch every reel at once.
    video.preload = 'none';
    video.dataset.hls = reel.url;        // adaptive .m3u8, preferred
    video.dataset.src = reel.fallback;   // progressive .mp4, the safety net

    // Poster layer (not <video poster>): a background image that loads eagerly and
    // stays put until the video is genuinely playing, so it covers the whole HLS
    // handshake + decode gap. Revealed by removing it via the `.is-played` class.
    const wrap = /** @type {HTMLElement} */ (el.querySelector('.ie__video-wrap'));
    if (reel.poster) {
      const posterEl = el.querySelector('[data-ie-poster]');
      if (posterEl) posterEl.style.backgroundImage = `url("${reel.poster}")`;
    }
    // Drop the poster only once a real frame is on screen (`playing` fires with
    // frames available), never before — so there's no black flash on the swap.
    video.addEventListener('playing', () => wrap.classList.add('is-played'), { once: true });

    this.#wireSeekBar(video, /** @type {HTMLInputElement} */ (el.querySelector('[data-ie-seek]')));

    // Base counts live on the element so the toggles can re-derive from them.
    el.dataset.likes = String(reel.likes);
    el.dataset.share = String(reel.share);
    this.#paintLike(el);
    this.#paintShare(el);

    // Caption: keep the full text on the element (the visible text gets truncated
    // later) and let #clampCaptions add the "more" toggle once it's in the DOM.
    const caption = el.querySelector('[data-ie-caption]');
    if (reel.caption) {
      caption.dataset.caption = reel.caption;
      caption.textContent = reel.caption;
    } else {
      caption.remove();
    }

    if (reel.content) {
      const shop = el.querySelector('[data-ie-shop]');
      if (shop) shop.hidden = false;
    }

    const chips = el.querySelector('[data-ie-chips]');
    for (const tag of reel.tags) {
      const chip = this.#clone('chip');
      chip.dataset.ieTag = tag;
      chip.textContent = `#${tag}`;
      chips.append(chip);
    }

    return el;
  }

  /* ----------------------------- sources --------------------------- */

  /**
   * Start loading a reel's video, once — called for the active reel and the one
   * right after it, so we never fetch every reel at the same time.
   *
   * Source preference:
   *   1. HLS (.m3u8) natively — Safari/iOS, no library needed
   *   2. HLS via hls.js       — everyone else, loaded on demand
   *   3. the .mp4 `fallback`  — no HLS anywhere, or no .m3u8 on this reel
   *
   * @param {Element|null} reel
   */
  #loadVideo(reel) {
    const video = /** @type {HTMLVideoElement|null} */ (reel?.querySelector?.('video'));
    if (!video || video.dataset.loaded === 'true') return;
    video.dataset.loaded = 'true'; // guard first: hls.js attaches async

    const hlsUrl = video.dataset.hls;
    const mp4Url = video.dataset.src;
    video.preload = 'auto';

    if (!hlsUrl) {
      video.src = mp4Url;
      return;
    }

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl; // native HLS
      return;
    }

    this.#ensureHls().then((Hls) => {
      if (!video.isConnected) return; // filtered away while the library loaded

      if (Hls && Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          // Never fetch a rung bigger than the frame actually shows: smaller first
          // segment => faster first frame, and less data on phones.
          capLevelToPlayerSize: true,
          startFragPrefetch: true,
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        video._hls = hls; // kept so #teardownVideos can destroy it

        // The manifest lands after we've already tried to play, so kick the active
        // reel once it's actually ready.
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          const el = video.closest('.ie__reel');
          if (el && el === this.#activeReel) this.#playReel(/** @type {HTMLElement} */ (el));
        });

        // If HLS dies unrecoverably, drop to the mp4 rather than showing nothing.
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data?.fatal) return;
          warn('HLS failed, falling back to mp4', data.type);
          hls.destroy();
          video._hls = null;
          if (mp4Url) video.src = mp4Url;
        });
      } else if (mp4Url) {
        video.src = mp4Url;
      }
    });
  }

  /**
   * Lazily inject hls.js (once per page) from the URL on `data-hls-src`.
   * Safari/iOS never hit this — they play HLS natively.
   * @returns {Promise<any>}
   */
  #ensureHls() {
    if (window.Hls) return Promise.resolve(window.Hls);

    const src = this.dataset.hlsSrc;
    if (!src) return Promise.resolve(null);

    IncludEngagementComponent.hlsLoader ??= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(window.Hls ?? null);
      script.onerror = reject;
      document.head.appendChild(script);
    }).catch(() => null);

    return IncludEngagementComponent.hlsLoader;
  }

  /** Pause videos and destroy any hls.js instances (disconnect / re-render). */
  #teardownVideos() {
    for (const video of this.#els.viewport?.querySelectorAll('video') ?? []) {
      video.pause();
      if (video._hls) {
        video._hls.destroy();
        video._hls = null;
      }
    }
  }

  /**
   * Two-way binding between a video and its (div-based) scrubber:
   *   video plays -> fill the bar
   *   drag / tap  -> seek the video forward / backward
   *   arrow keys  -> nudge ±5%
   * @param {HTMLVideoElement} video
   * @param {HTMLElement} seek  the .ie__seek element
   */
  #wireSeekBar(video, seek) {
    if (!seek) return;

    const track = seek.querySelector('.ie__seek__track') ?? seek;
    const fill = seek.querySelector('[data-ie-seek-fill]');
    let dragging = false;

    /** Paint the bar to a 0–100 percentage (the thumb is the fill's ::after). */
    const paint = (percent) => {
      const clamped = Math.min(100, Math.max(0, percent));
      if (fill) fill.style.width = `${clamped}%`;
      seek.setAttribute('aria-valuenow', String(Math.round(clamped)));
    };

    /** Seek the video from a pointer x-position. */
    const seekTo = (clientX) => {
      const rect = track.getBoundingClientRect();
      const ratio = rect.width ? (clientX - rect.left) / rect.width : 0;
      paint(ratio * 100);
      if (video.duration) video.currentTime = Math.min(1, Math.max(0, ratio)) * video.duration;
    };

    // Playback -> bar (skip while dragging so the thumb doesn't fight the finger).
    video.addEventListener('timeupdate', () => {
      if (!video.duration || dragging) return;
      paint((video.currentTime / video.duration) * 100);
    });

    // Drag / tap -> playback. `is-scrubbing` is what reveals the thumb (CSS) —
    // it stays hidden during plain playback.
    seek.addEventListener('pointerdown', (event) => {
      dragging = true;
      seek.classList.add('is-scrubbing');
      seek.setPointerCapture(event.pointerId);
      seekTo(event.clientX);
    });
    seek.addEventListener('pointermove', (event) => dragging && seekTo(event.clientX));
    const endDrag = (event) => {
      dragging = false;
      seek.classList.remove('is-scrubbing');
      if (seek.hasPointerCapture?.(event.pointerId)) seek.releasePointerCapture(event.pointerId);
    };
    seek.addEventListener('pointerup', endDrag);
    seek.addEventListener('pointercancel', endDrag);

    // Keyboard support (the bar is focusable via role="slider" + tabindex).
    seek.addEventListener('keydown', (event) => {
      if (!video.duration) return;
      const step = video.duration * 0.05;
      if (event.key === 'ArrowRight') video.currentTime = Math.min(video.duration, video.currentTime + step);
      else if (event.key === 'ArrowLeft') video.currentTime = Math.max(0, video.currentTime - step);
      else return;
      event.preventDefault();
      paint((video.currentTime / video.duration) * 100);
    });
  }

  /* --------------------------- interaction ------------------------- */

  /** Single delegated click handler for the whole component. */
  /**
   * Fire a `reels_action` analytics event. The event bubbles to document, where
   * custom-ga-events.js is the ONE place that forwards it to GA — this component
   * stays analytics-agnostic and just names the action.
   * @param {string} actionName  e.g. 'video_like' | 'video_share' | 'video_hashtag_click' | 'video_scroll'
   * @param {Element|null} reelEl  the reel the action belongs to (its id is the reel_id)
   * @param {Record<string, unknown>} [extra]  extra payload fields (e.g. { tag_name })
   */
  #emit(actionName, reelEl, extra) {
    const reelId = /** @type {HTMLElement|null} */ (reelEl)?.dataset?.id;
    if (!reelId) return; // e.g. the end card carries no id — nothing to attribute
    this.dispatchEvent(
      new CustomEvent('reels_action', {
        bubbles: true,
        detail: { action_name: actionName, reel_id: reelId, ...extra },
      })
    );
  }

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Tag pills (top filter chips and per-reel hashtags both carry data-ie-tag).
    const tagEl = target.closest('[data-ie-tag]');
    if (tagEl) {
      const tag = tagEl.dataset.ieTag;
      // A real hashtag click (reel overlay tag or top filter chip) — not the
      // "All" reset (ALL_TAG) and not the end-card jump chips.
      const isHashtag = tag !== ALL_TAG && (tagEl.classList.contains('ie__tag') || tagEl.classList.contains('ie__chip'));
      if (isHashtag) {
        this.#emit('video_hashtag_click', tagEl.closest('.ie__reel') ?? this.#activeReel, { tag_name: tag });
      }
      return this.#applyFilter(tag);
    }

    if (target.closest('[data-ie-back]')) return this.#goBack();
    if (target.closest('[data-ie-mute]')) return this.#toggleMute();

    const reel = /** @type {HTMLElement|null} */ (target.closest('.ie__reel'));
    if (!reel) return;

    if (target.closest('[data-ie-like]')) return this.#toggleLike(reel);
    if (target.closest('[data-ie-share]')) return this.#share(reel);
    if (target.closest('[data-ie-playpause]')) return this.#togglePlayback(reel);
  };

  /**
   * Back button: go back if we arrived from somewhere on this site; otherwise
   * (opened cold from a shared link, a new tab, etc.) send them to the home page
   * so the button is never a dead end.
   */
  #goBack() {
    let cameFromSameSite = false;
    try {
      cameFromSameSite = !!document.referrer && new URL(document.referrer).origin === window.location.origin;
    } catch {
      cameFromSameSite = false;
    }

    if (cameFromSameSite) window.history.back();
    else window.location.assign('/');
  }

  /** @param {HTMLElement} reel */
  async #openContentSheet(reel) {
    const content = this.#contentById.get(reel.dataset.id);
    const sheet = this.#els.sheet;
    if (!content || !sheet) return;

    this.#emit('video_shop_open', reel, { content_type: content.type });

    const list = sheet.querySelector('[data-ie-sheet-list]');
    list.replaceChildren();
    sheet.querySelector('[data-ie-sheet-title]').textContent = ContentPreview.labelFor(content.type);
    sheet.hidden = false;
    requestAnimationFrame(() => sheet.classList.add('is-open')); // next frame, so the transition actually runs

    const previews = await Promise.all(content.values.map((path) => ContentPreview.fetch(path)));
    if (!sheet.classList.contains('is-open')) return; // closed, or scrolled to another reel, while these loaded

    list.replaceChildren(...previews.filter(Boolean).map((preview) => this.#buildSheetCard(preview)));
    if (!list.children.length) list.append(this.#clone('sheetempty'));
  }

  #closeContentSheet() {
    const sheet = this.#els.sheet;
    if (!sheet || sheet.hidden) return;
    sheet.classList.remove('is-open');
    setTimeout(() => { sheet.hidden = true; }, 200); // matches the CSS slide-down duration
  }

  /** @param {{title: string, image: string, url: string, price: string|null}} preview */
  #buildSheetCard(preview) {
    const card = this.#clone('sheetcard');
    card.href = preview.url;
    const img = card.querySelector('[data-ie-sheet-image]');
    if (preview.image) img.style.backgroundImage = `url("${preview.image}")`;
    card.querySelector('[data-ie-sheet-name]').textContent = preview.title;

    const price = card.querySelector('[data-ie-sheet-price]');
    if (preview.price) price.textContent = preview.price;
    else price.remove();

    card.addEventListener('click', () => this.#emit('video_shop_click', this.#activeReel, { url: preview.url }));
    return card;
  }

  /** 
   * Fetches product data and populates the inline carousel for the active reel.
   * @param {HTMLElement} reel 
   */
  async #loadCarousel(reel) {
    if (reel.dataset.carouselLoaded === 'true') return;

    const content = this.#contentById.get(reel.dataset.id);
    const carousel = reel.querySelector('[data-ie-carousel]');
    
    if (!content || !carousel) return;

    reel.dataset.carouselLoaded = 'true';

    // TYPE 1: Handle Shoppable Products
    if (content.type === 'PRODUCTS') {
      const previews = await Promise.all(content.values.map((path) => ContentPreview.fetch(path)));
      const validPreviews = previews.filter(Boolean);

      if (validPreviews.length === 0) return;

      const cards = validPreviews.map((preview) => this.#buildProductCard(preview, reel));
      carousel.replaceChildren(...cards);
      carousel.hidden = false;
    } 
    // TYPE 2: Handle Collections and Blogs
    else if (content.type === 'COLLECTION_ID' || content.type === 'BLOG') {
      // Content values for blogs/collections contain the destination URL
      const targetUrl = content.values[0];
      if (!targetUrl) return;

      // Get the appropriate label ("Featured collection" or "From the blog")
      const labelText = ContentPreview.labelFor(content.type);

      // Build the solo button
      const ctaBtn = document.createElement('a');
      ctaBtn.className = 'ie__shoplink ie__shoplink--solo';
      ctaBtn.href = targetUrl;
      ctaBtn.innerHTML = `${labelText}<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      // Track the click event
      ctaBtn.addEventListener('click', () => this.#emit('video_shop_click', reel, { url: targetUrl }));

      // Append and unhide
      carousel.replaceChildren(ctaBtn);
      carousel.hidden = false;
    }
  }

  /** 
   * Clones the pcard template and maps the fetched product data to the HTML.
   * @param {Object} preview 
   * @param {HTMLElement} activeReel
   */
   #buildProductCard(preview, activeReel) {
    const card = this.#clone('pcard');
    card.href = preview.url;

    // Inject Image
    const img = card.querySelector('[data-ie-pcard-img]');
    if (preview.image) {
        img.src = preview.image;
    }

    // Inject Title
    const nameEl = card.querySelector('[data-ie-pcard-name]');
    if (nameEl) nameEl.textContent = preview.title;

    // Inject Price
    const priceEl = card.querySelector('[data-ie-pcard-price]');
    if (preview.price) {
        priceEl.textContent = preview.price;
    } else {
        priceEl.remove();
    }

    // Inject Compare Price (Strikethrough)
    // const compareEl = card.querySelector('[data-ie-pcard-compare]');
    // if (preview.compare) {
    //     compareEl.textContent = preview.compare;
    // } else {
    //     compareEl.remove();
    // }

    // Attach Analytics Click Event
    card.addEventListener('click', () => this.#emit('video_shop_click', activeReel, { url: preview.url }));
    
    return card;
   }
  /** @param {string} tag */
  #applyFilter(tag) {
    if (this.#activeTag === tag) return;
    this.#activeTag = tag;
    this.#activeReel = null;
    log('filter ->', tag);
    this.#renderTabs();
    this.#renderReels();
    this.#closeContentSheet();
  }

  #toggleMute() {
    this.#muted = !this.#muted;
    this.#els.mute?.classList.toggle('is-muted', this.#muted);
    const video = this.#activeReel?.querySelector('video');
    if (video) video.muted = this.#muted;
  }

  /** @param {HTMLElement} reel */
  #toggleLike(reel) {
    const id = reel.dataset.id;
    if (!id) return;

    const wasLiked = this.#liked.has(id);
    if (wasLiked) this.#liked.delete(id);
    else this.#liked.add(id);

    LikeStore.save(this.#liked); // client-side only — never leaves the browser
    this.#paintLike(reel);

    // GA: only on a genuine like (not unlike), and at most once per reel/session.
    if (!wasLiked && !this.#emittedLike.has(id)) {
      this.#emittedLike.add(id);
      this.#emit('video_like', reel);
    }
  }

  /**
   * Paint a reel's like button from state. The count is always DERIVED:
   *   base (from the metafield) + (liked ? 1 : 0)
   * Never incremented/decremented, so re-renders and edits to the JSON can't
   * make it drift.
   * @param {HTMLElement} reel
   */
  #paintLike(reel) {
    const id = reel.dataset.id;
    const base = Number(reel.dataset.likes) || 0;
    const liked = this.#liked.has(id);

    const button = reel.querySelector('[data-ie-like]');
    if (!button) return;
    button.classList.toggle('is-liked', liked);
    button.setAttribute('aria-pressed', String(liked));

    const count = button.querySelector('[data-ie-like-count]');
    if (count) count.textContent = formatCount(base + (liked ? 1 : 0));
  }

  /**
   * Share a reel. Unlike a like, this only ever counts UP — every tap is a share.
   * The tally is bumped on tap (not on a successful send), because the OS share
   * sheet gives no reliable "did they actually send it" signal, and a count that
   * only moves sometimes reads as broken.
   * @param {HTMLElement} reel
   */
  async #share(reel) {
    const id = reel.dataset.id;
    if (!id) return;

    this.#shared.set(id, (this.#shared.get(id) ?? 0) + 1);
    ShareStore.save(this.#shared); // client-side only — never leaves the browser
    this.#paintShare(reel);
    this.#emit('video_share', reel);

    // Share that reel's own link, not just whatever the address bar happens to show.
    const url = withVideoParam(window.location.href, id);
    try {
      if (navigator.share) await navigator.share({ title: document.title, url });
      else await navigator.clipboard?.writeText(url);
    } catch (error) {
      log('share dismissed / failed', error);
    }
  }

  /**
   * Paint a reel's share count. DERIVED, like the like count:
   *   base (from the metafield) + times this visitor shared it
   * @param {HTMLElement} reel
   */
  #paintShare(reel) {
    const id = reel.dataset.id;
    const base = Number(reel.dataset.share) || 0;
    const mine = this.#shared.get(id) ?? 0;

    const count = reel.querySelector('[data-ie-share-count]');
    if (count) count.textContent = formatCount(base + mine);
  }

  /* --------------------------- playback ---------------------------- */

  #observe() {
    this.#observer?.disconnect();
    const viewport = this.#els.viewport;
    if (!viewport) return;

    // Play whichever reel is dominantly in view; pause the rest.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            // `true` = this came from an actual scroll (drives the video_scroll event).
            this.#setActiveReel(/** @type {HTMLElement} */ (entry.target), true);
          }
        }
      },
      { root: viewport, threshold: [0, 0.6, 1] }
    );

    for (const reel of viewport.querySelectorAll('.ie__reel')) this.#observer.observe(reel);

    // Observer fires async — start the first reel right away.
    const first = viewport.querySelector('.ie__reel');
    if (first) this.#setActiveReel(/** @type {HTMLElement} */ (first));
  }

  /** @param {HTMLElement} reel */
  #setActiveReel(reel, fromScroll = false) {
    if (this.#activeReel === reel) return;
    this.#closeContentSheet();
    log('active reel', reel.dataset.id);

    // Cheap + immediate: stop the old video so no audio bleeds while we scroll.
    if (this.#activeReel) {
      this.#activeReel.classList.remove('is-active', 'is-paused');
      this.#activeReel.querySelector('video')?.pause();
    }

    this.#activeReel = reel;
    reel.classList.add('is-active');

    // Everything below is main-thread work that makes scrolling stutter if it runs
    // for every reel you flick past: loading a source (spins up an hls.js instance),
    // video.play() (decoder start), and history.replaceState() — which Safari throws
    // on past ~100 calls/30s. So wait for the scroll to settle, then do it once.
    // The poster is already painted, so the reel never looks empty meanwhile.
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      if (this.#activeReel !== reel) return; // moved on since — this reel lost the race
      this.#loadVideo(reel);
      this.#loadVideo(reel.nextElementSibling); 
      
      this.#loadCarousel(reel);

      this.#playReel(reel);
      this.#syncUrl(reel.dataset.id);
      // GA: only for real scrolls — not the initial open or filter re-renders
      // (those come through #observe's direct call with fromScroll = false).
      if (fromScroll) this.#emit('video_scroll', reel);
    }, SETTLE_MS);
  }

  /**
   * Reflect the currently playing reel in the URL as `?video=<id>`, using
   * replaceState so there's no reload and no back-button history spam.
   * @param {string|undefined} id
   */
  #syncUrl(id) {
    if (id == null || id === this.#syncedId) return;
    this.#syncedId = id;
    window.history.replaceState(window.history.state, '', withVideoParam(window.location.href, id));
  }

  /** @param {HTMLElement|null} reel */
  #playReel(reel) {
    const video = reel?.querySelector('video');
    if (!video) return;
    video.muted = this.#muted;
    video.play()?.catch((error) => {
      log('autoplay blocked, showing paused state', error);
      reel?.classList.add('is-paused');
    });
  }

  /** @param {HTMLElement} reel */
  #togglePlayback(reel) {
    const video = reel.querySelector('video');
    if (!video) return;
    if (video.paused) {
      reel.classList.remove('is-paused');
      this.#playReel(reel);
    } else {
      video.pause();
      reel.classList.add('is-paused');
    }
  }
}

if (!customElements.get('includ-engagement-component')) {
  customElements.define('includ-engagement-component', IncludEngagementComponent);
}

export { IncludEngagementComponent, EngagementData, shuffle, withVideoParam };
